/**
 * UnreadBadgeContext — provides per-section "mark as seen" callbacks plus
 * the current unread ID sets to the whole user/admin panel subtree.
 *
 * Why a context: OrdersTable, OrderDetailDrawer, transaction rows etc.
 * all live many levels deep. Threading `markOrderSeen` + `unreadOrderIds`
 * through every prop would be noisy. The panel root sets up the trackers
 * and publishes their seen-callbacks plus unread sets; deep components
 * import the hook and call `markSeen` on click and render row-level
 * "NEW" pills for IDs in the unread set.
 *
 * Provider shape:
 *   {
 *     markOrderSeen: (id) => void,
 *     markTransactionSeen: (id) => void,
 *     markTicketSeen: (orderId) => void,         // not used yet
 *     unreadOrderIds: Set<string>,
 *     unreadTransactionIds: Set<string>,
 *   }
 *
 * Outside the provider every callback is a no-op and unread sets are
 * empty, so components are safe to use unconditionally.
 */
import React, { createContext, useContext, useMemo } from 'react';

const NOOP = () => {};
const EMPTY_SET = new Set();

const UnreadBadgeContext = createContext({
  markOrderSeen: NOOP,
  markTransactionSeen: NOOP,
  markTicketSeen: NOOP,
  unreadOrderIds: EMPTY_SET,
  unreadTransactionIds: EMPTY_SET,
});

export function UnreadBadgeProvider({ value, children }) {
  // Memoize the context value so consumers don't re-render unless the
  // underlying state actually changed. The caller passes a new object
  // each render; we stabilise by destructuring the relevant fields.
  const stable = useMemo(() => ({
    markOrderSeen: value?.markOrderSeen || NOOP,
    markTransactionSeen: value?.markTransactionSeen || NOOP,
    markTicketSeen: value?.markTicketSeen || NOOP,
    unreadOrderIds: value?.unreadOrderIds || EMPTY_SET,
    unreadTransactionIds: value?.unreadTransactionIds || EMPTY_SET,
  }), [
    value?.markOrderSeen,
    value?.markTransactionSeen,
    value?.markTicketSeen,
    value?.unreadOrderIds,
    value?.unreadTransactionIds,
  ]);
  return (
    <UnreadBadgeContext.Provider value={stable}>{children}</UnreadBadgeContext.Provider>
  );
}

export function useUnreadBadges() {
  return useContext(UnreadBadgeContext);
}

